import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';
import {
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  UserCredential,
  signInWithCustomToken,
} from 'firebase/auth';
import { auth } from '../../../api/firebase/config';
import { recoverMfaWithBackupCode } from '../../../api/firebase/function/security/securityMfaFunction';
import { recordClientAuditEvent } from '../../../api/firebase/function/security/clientAuditEventFunction';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import {
  auditAction,
  auditResourceType,
  MFA_BACKUP_CODE_LENGTH,
  MFA_SESSION_EXPIRED_AUTH_ERROR_CODES,
  MFA_TOTP_CODE_LENGTH,
} from '../../../constants/enum';
import {
  gray40,
  gray60,
  primaryButtonBackgroundColor,
  red20,
  red50,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularXs } from '../../../constants/location';
import CustomTextfield from '../form/CustomTextfield';

type View = 'code' | 'recovery';

const MFA_SESSION_EXPIRED_MESSAGE =
  'セッションの有効期限が切れました。もう一度ログインしてください。';

/**
 * MFA ペンディングセッション（第一要素の証明）の期限切れ・無効エラーか判定する（#3105）。
 * この状態はコードを入れ直しても解消しないため、再ログインへ誘導する必要がある。
 */
/**
 * ログイン成功を監査ログに記録する（#2985・#3122）。
 * recordClientAuditEvent は内部で例外を握り潰すため、ログイン自体は失敗させない。
 */
const recordLoginAudit = async (): Promise<void> => {
  await recordClientAuditEvent({
    action: auditAction.login,
    resource_type: auditResourceType.session,
  });
};

const isMfaSessionExpiredError = (error: FirebaseError): boolean =>
  MFA_SESSION_EXPIRED_AUTH_ERROR_CODES.some(code => code === error.code);

interface Props {
  open: boolean;
  /** signInWithEmailAndPassword が multi-factor-auth-required を返したときの resolver */
  resolver: MultiFactorResolver | null;
  /** バックアップコードログイン（第一要素の再検証）に使うログインフォームの入力値 */
  email: string;
  password: string;
  /**
   * バックアップコードでのログインを提示するか（既定: true・#3166）。
   * SSO ユーザーはパスワードを持たず第一要素の再検証ができないため false にする
   */
  backupCodeAvailable?: boolean;
  /**
   * 確認コードの検証に成功したあとの後処理（#3166）。
   * SSO ログインは JIT プロビジョニングと遷移が残っているため呼び出し元で続ける。
   * 指定された場合は監査ログの記録とダイアログのクローズも呼び出し元の責務とする
   */
  onSuccess?: (credential: UserCredential) => Promise<void>;
  onClose: () => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  bodyText: {
    ...dialogText,
    marginBottom: '16px',
  },
  caption: {
    ...regularXs,
    color: gray60,
    marginTop: '16px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    marginBottom: '16px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  actionButton: {
    height: '44px',
    minWidth: '140px',
  },
};

/**
 * ログイン時の二要素認証ダイアログ（#2984）。
 * 認証アプリの 6 桁コードで第二要素を解決する。
 * 認証アプリを使用できない場合はバックアップコードでログインできる（PRD §5-3・#3123）。
 * バックアップコードは第二要素の代わりに「そのログインだけ」を通すもので、
 * サーバーが発行したカスタムトークンでサインインする。認証アプリの登録は維持される。
 */
const MfaSignInDialog: React.FC<Props> = ({
  open,
  resolver,
  email,
  password,
  backupCodeAvailable = true,
  onSuccess,
  onClose,
}) => {
  const [view, setView] = useState<View>('code');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // MFA ペンディングセッションが期限切れになった状態（コード再入力では解消しない）
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (open) {
      setView('code');
      setCode('');
      setBackupCode('');
      setErrorMessage('');
      setSessionExpired(false);
    }
  }, [open]);

  const handleVerifyCode = async (): Promise<void> => {
    if (!resolver) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const totpHint = resolver.hints.find(
        hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      if (!totpHint) {
        setErrorMessage('この端末で利用できる二要素認証がありません。');
        return;
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
      const credential = await resolver.resolveSignIn(assertion);
      if (onSuccess) {
        // SSO（#3166）: 監査ログ・クローズを含む後処理は呼び出し元の SAML フローで行う
        await onSuccess(credential);
        return;
      }
      // 監査ログ（#2985）: MFA 経由のログイン成功（#3122）。
      // ログイン画面の signInWithEmailAndPassword は multi-factor-auth-required で
      // throw するため、実際のログイン完了地点であるここで記録する
      await recordLoginAudit();
      // 成功時は AuthContext の user 更新でホームへ遷移する
      onClose();
    } catch (e) {
      if (e instanceof FirebaseError) {
        if (isMfaSessionExpiredError(e)) {
          // 期限切れは再ログインでしか解消しないため、汎用エラーではなく専用の案内を出す（#3105）
          setSessionExpired(true);
          setErrorMessage(MFA_SESSION_EXPIRED_MESSAGE);
          return;
        }
        setErrorMessage(
          e.code === 'auth/invalid-verification-code'
            ? '確認コードが正しくありません。認証アプリに表示されている最新のコードを入力してください。'
            : getErrorMessageFromAuthErrorCode(e.code),
        );
      } else {
        Sentry.captureException(e);
        setErrorMessage('二要素認証に失敗しました。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecover = async (): Promise<void> => {
    setSubmitting(true);
    setErrorMessage('');
    try {
      const result = await recoverMfaWithBackupCode({
        email,
        password,
        backup_code: backupCode.trim(),
      });
      // 第二要素の登録は維持したまま、このログインだけをカスタムトークンで通す（#3123）
      await signInWithCustomToken(auth, result.custom_token);
      // 監査ログ（#2985・#3122）はサーバー側（recoverMfaWithBackupCode）で
      // login:session として記録するため、ここでは記録しない（#3123・二重記録の防止）
      onClose();
    } catch (e) {
      if (e instanceof FirebaseError) {
        // callable のエラーメッセージ（日本語）をそのまま表示する
        setErrorMessage(e.message || 'バックアップコードでのログインに失敗しました。');
      } else {
        Sentry.captureException(e);
        setErrorMessage('バックアップコードでのログインに失敗しました。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>二要素認証</Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}

      {view === 'code' ? (
        <>
          <Typography sx={styles.bodyText}>
            {sessionExpired
              ? 'ログインからの時間が経過したため、二要素認証の有効期限が切れました。確認コードを入力し直しても認証できないため、ログイン画面からやり直してください。'
              : `認証アプリに表示されている${MFA_TOTP_CODE_LENGTH}桁の確認コードを入力してください。`}
          </Typography>
          {!sessionExpired && (
            <>
              <CustomTextfield
                labelText={`確認コード（${MFA_TOTP_CODE_LENGTH}桁）`}
                inputWidth="100%"
                fontSizeIs14px
                placeholder="123456"
                value={code}
                onChange={(e): void => setCode(e.target.value)}
                formMb="0"
              />
              {backupCodeAvailable && (
                <Typography sx={styles.caption}>
                  認証アプリを使用できない場合は
                  <Link
                    component="button"
                    type="button"
                    onClick={(): void => {
                      setErrorMessage('');
                      setView('recovery');
                    }}
                  >
                    バックアップコードでログイン
                  </Link>
                  できます。
                </Typography>
              )}
            </>
          )}
          <Box sx={styles.dialogActions}>
            {sessionExpired ? (
              <Button onClick={onClose} sx={[primaryButtonBackgroundColor, styles.actionButton]}>
                ログイン画面に戻る
              </Button>
            ) : (
              <Button
                onClick={handleVerifyCode}
                disabled={submitting || code.trim().length !== MFA_TOTP_CODE_LENGTH}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={[primaryButtonBackgroundColor, styles.actionButton]}
              >
                ログイン
              </Button>
            )}
          </Box>
        </>
      ) : (
        <>
          <Typography sx={styles.bodyText}>
            認証アプリの設定時に保存したバックアップコード（{MFA_BACKUP_CODE_LENGTH}桁）を
            入力してください。コード1個につき1回ログインできます。
            認証アプリの設定はそのまま維持されるため、QRコードの読み取り直しは不要です。
          </Typography>
          <CustomTextfield
            labelText={`バックアップコード（${MFA_BACKUP_CODE_LENGTH}桁）`}
            inputWidth="100%"
            fontSizeIs14px
            placeholder="12345678"
            value={backupCode}
            onChange={(e): void => setBackupCode(e.target.value)}
            formMb="0"
          />
          <Typography sx={styles.caption}>
            <Link
              component="button"
              type="button"
              onClick={(): void => {
                setErrorMessage('');
                setView('code');
              }}
            >
              確認コードの入力に戻る
            </Link>
          </Typography>
          <Box sx={styles.dialogActions}>
            <Button
              onClick={handleRecover}
              disabled={submitting || backupCode.trim().length !== MFA_BACKUP_CODE_LENGTH}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={[primaryButtonBackgroundColor, styles.actionButton]}
            >
              ログイン
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default MfaSignInDialog;
